import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'

import { TApi } from '../../../../shared/const'
import http from '../../../../shared/http'
import { askConfirm } from '../../../../shared/confirmDialog'
import { formatVienna } from '../../../../shared/datetime'

import Btn from '../../../../UI/Btn'

import styles from './pendingInvitations.module.scss'

/**
 * Die offenen Einladungen der eigenen Junior Company (#248).
 *
 * Eine eingeladene Person ist erst eine Expert:in, wenn sie die Einladung
 * annimmt. Bis dahin stand sie auf dieser Seite nirgends: Wer nachsehen wollte,
 * ob der Onkel schon eingeladen ist, konnte es nur durch erneutes Einladen —
 * und bekam dann „Diese E-Mail-Adresse lässt sich nicht einladen". Daraufhin
 * entstand eine zweite Einladung an eine vertippte Adresse.
 *
 * Jetzt steht jede offene Einladung hier, mit „Erneut senden" (neuer Link, die
 * Frist beginnt neu) und „Zurückziehen" (etwa für eine vertippte Adresse).
 * Gibt es keine, bleibt der Block weg.
 *
 * `refreshKey` lädt neu, wenn die Ansicht gerade eingeladen hat.
 */
export default function PendingInvitations({ companyId, refreshKey }) {
	const { t } = useTranslation()

	const [rows, setRows] = useState([])
	const [busyId, setBusyId] = useState(null)

	const load = useCallback(() => {
		http.get(TApi.COMMON_SUPPORTER_INVITATIONS, {
			params: companyId ? { companyId } : {},
			disableToast: true
		})
			.then(({ data }) => setRows(data?.rows || []))
			// Ein Ladefehler soll die Expert:innen-Liste nicht mit einer Meldung
			// überdecken; der Block fehlt dann einfach.
			.catch(() => setRows([]))
	}, [companyId])

	useEffect(() => {
		load()
	}, [load, refreshKey])

	if (!rows.length) return null

	const name = row => [row.firstName, row.lastName].filter(Boolean).join(' ') || row.email

	const resend = row => {
		setBusyId(row.id)
		http.post(TApi.COMMON_SUPPORTER_INVITATION_RESEND(row.id))
			.then(({ data }) => {
				if (data?.emailSent === false) toast.warning(t('Invitation saved, but the email could not be sent'))
				else toast.success(t('pendingInvitationResent', { name: name(row) }))
				load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setBusyId(null))
	}

	const withdraw = async row => {
		const ok = await askConfirm({
			title: t('pendingInvitationWithdrawTitle'),
			text: t('pendingInvitationWithdrawText', { name: name(row), email: row.email }),
			confirmLabel: t('pendingInvitationWithdraw'),
			tone: 'danger'
		})
		if (!ok) return

		setBusyId(row.id)
		http.delete(TApi.COMMON_SUPPORTER_INVITATION(row.id))
			.then(() => {
				toast.success(t('pendingInvitationWithdrawn'))
				load()
			})
			.catch(() => {}) // Meldung kommt aus shared/http
			.finally(() => setBusyId(null))
	}

	return (
		<section className={styles.root} aria-label={t('pendingInvitationsTitle')}>
			<h3 className={styles.title}>
				{t('pendingInvitationsTitle')} <span className={styles.count}>{rows.length}</span>
			</h3>
			<p className={styles.hint}>{t('pendingInvitationsHint')}</p>
			<ul className={styles.list}>
				{rows.map(row => (
					<li key={row.id} className={styles.row}>
						<div className={styles.who}>
							<strong className={styles.name}>{name(row)}</strong>
							<span className={styles.email}>{row.email}</span>
						</div>
						<div className={styles.meta}>
							<span>
								{row.invitedByName
									? t('pendingInvitationSentBy', {
											date: formatVienna(row.invitedAt, 'dd.MM.yyyy'),
											name: row.invitedByName
									  })
									: t('pendingInvitationSent', { date: formatVienna(row.invitedAt, 'dd.MM.yyyy') })}
							</span>
							<span className={row.expired ? styles.expired : undefined}>
								{row.expired
									? t('pendingInvitationExpired')
									: t('pendingInvitationValidUntil', {
											date: formatVienna(row.expiresAt, 'dd.MM.yyyy')
									  })}
							</span>
						</div>
						<div className={styles.actions}>
							<Btn
								size="small"
								theme="secondary"
								disabled={busyId === row.id}
								onClick={() => resend(row)}
							>
								{t('pendingInvitationResend')}
							</Btn>
							<Btn
								size="small"
								theme="secondary"
								disabled={busyId === row.id}
								onClick={() => withdraw(row)}
							>
								{t('pendingInvitationWithdraw')}
							</Btn>
						</div>
					</li>
				))}
			</ul>
		</section>
	)
}

PendingInvitations.propTypes = {
	companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	refreshKey: PropTypes.number
}
